// Typed wrappers over the foundry-metabase reports API.
// Endpoint paths match the FastAPI routes in:
//   src/foundry_metabase/control_plane/page_api.py
//   src/foundry_metabase/control_plane/reports_api.py
//   src/foundry_metabase/control_plane/member_api.py

import { api } from "./client";
import type {
  AccessAuditEvent,
  AccessMatrix,
  EmbedURLResponse,
  TenantBranding,
  TenantMember,
  TenantPage,
  TenantPageFeature,
} from "../types";

// ── Branding (public) ─────────────────────────────────────────
export const getBranding = (slug: string): Promise<TenantBranding> =>
  api(`/api/tenants/${slug}/branding`, { redirectOn401: false });

// ── Members ───────────────────────────────────────────────────
export const listMembers = (slug: string): Promise<TenantMember[]> =>
  api(`/api/tenants/${slug}/members`);

export const inviteMember = (
  slug: string,
  payload: { email: string; role: TenantMember["role"] },
): Promise<TenantMember> =>
  api(`/api/tenants/${slug}/members`, { method: "POST", body: payload });

export const revokeMember = (slug: string, memberId: number): Promise<TenantMember> =>
  api(`/api/tenants/${slug}/members/${memberId}`, { method: "DELETE" });

// ── Pages ─────────────────────────────────────────────────────
export const listPages = (slug: string): Promise<TenantPage[]> =>
  api(`/api/tenants/${slug}/pages`);

export const getPage = (slug: string, pageId: number): Promise<TenantPage> =>
  api(`/api/tenants/${slug}/pages/${pageId}`);

export interface CreatePageBody {
  slug: string;
  name: string;
  description?: string | null;
  sort_order?: number;
  metabase_dashboard_id?: number | null;
  metabase_collection_id?: number | null;
}

export const createPage = (
  tenantSlug: string,
  body: CreatePageBody,
): Promise<TenantPage> =>
  api(`/api/tenants/${tenantSlug}/pages`, { method: "POST", body });

export interface CreateFeatureBody {
  slug: string;
  name: string;
  description?: string | null;
  sort_order?: number;
  metabase_card_id?: number | null;
}

export const createFeature = (
  tenantSlug: string,
  pageId: number,
  body: CreateFeatureBody,
): Promise<TenantPageFeature> =>
  api(`/api/tenants/${tenantSlug}/pages/${pageId}/features`, {
    method: "POST",
    body,
  });

export const listPageFeatures = (
  slug: string,
  pageId: number,
): Promise<TenantPageFeature[]> =>
  api(`/api/tenants/${slug}/pages/${pageId}/features`);

// ── Access matrix (admin person detail; also the source of truth
//                   for "what can the current end-user see") ────
export const getAccessMatrix = (
  slug: string,
  memberId: number,
): Promise<AccessMatrix> =>
  api(`/api/tenants/${slug}/members/${memberId}/access`);

// Self-only matrix — viewer-callable; the backend resolves the member
// from the session email so users cannot fish for others' access data.
export const getMyAccess = (slug: string): Promise<AccessMatrix> =>
  api(`/api/tenants/${slug}/my-access`);

export const setPageAccess = (
  slug: string,
  memberId: number,
  pageId: number,
  granted: boolean,
): Promise<AccessMatrix> =>
  api(`/api/tenants/${slug}/members/${memberId}/access/pages/${pageId}`, {
    method: "PUT",
    body: { granted },
  });

export const setFeatureAccess = (
  slug: string,
  memberId: number,
  featureId: number,
  granted: boolean,
): Promise<AccessMatrix> =>
  api(`/api/tenants/${slug}/members/${memberId}/access/features/${featureId}`, {
    method: "PUT",
    body: { granted },
  });

// ── Audit ─────────────────────────────────────────────────────
export const listAudit = (
  slug: string,
  limit = 200,
): Promise<AccessAuditEvent[]> =>
  api(`/api/tenants/${slug}/audit?limit=${limit}`);

// ── Embed URL (Phase D) ──────────────────────────────────────
export const mintEmbedUrl = (
  slug: string,
  pageId: number,
): Promise<EmbedURLResponse> =>
  api(`/api/tenants/${slug}/pages/${pageId}/embed-url`, { method: "POST" });
